import { useEffect, useState, useRef, useMemo, useCallback, Component } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Loader2, Lock, ShieldCheck, FileText, CreditCard, Flame, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { getTeaser, createOrder, captureOrder, getConfig, createStripeSession, getStripeStatus, prepareCheckout, validateCoupon, redeemCoupon } from "@/lib/api";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const PP_STYLE = { layout: "vertical", color: "gold", shape: "rect", label: "pay" };

class PayPalBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; this.onErr = this.onErr.bind(this); }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidMount() {
    window.addEventListener("error", this.onErr, true);
    window.addEventListener("unhandledrejection", this.onErr, true);
  }
  componentWillUnmount() {
    window.removeEventListener("error", this.onErr, true);
    window.removeEventListener("unhandledrejection", this.onErr, true);
  }
  onErr(e) {
    const msg = String((e && (e.message || (e.reason && (e.reason.message || e.reason)))) || "");
    const src = String((e && (e.filename || (e.reason && e.reason.stack))) || "");
    if (/zoid|destroyed all components/i.test(msg) || /paypal\.com|\/smart\/buttons/i.test(src)) {
      if (e && e.preventDefault) e.preventDefault(); // async SDK error: show fallback, don't crash the page
      if (!this.state.failed) this.setState({ failed: true });
    }
  }
  componentDidCatch() {}
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

function formatPrice(amount, currency) {
  const n = parseFloat(amount);
  if (currency === "USD") return `$${n.toFixed(2)}`;
  if (currency === "GBP") return `£${n.toFixed(2)}`;
  return `${n.toFixed(2).replace(".", ",")} €`;
}

const STATUS_COLOR = { green: "#10C98D", yellow: "#F59E0B", red: "#F43F5E" };
const STATUS_EMOJI = { green: "🟢", yellow: "🟡", red: "🔴" };

const emptyBuyer = {
  customer_type: "private",
  first_name: "", last_name: "", email: "", company: "", vat_id: "",
  street: "", zip: "", city: "", country: "Deutschland",
};

export default function Result() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();

  const initial = location.state?.signals
    ? { intro: location.state.intro, signals: location.state.signals, blocked: location.state.blocked, paid: false, details_completed: false }
    : null;
  const [teaserData, setTeaserData] = useState(initial);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(!initial);
  const [processing, setProcessing] = useState(false);
  const [buyer, setBuyer] = useState(emptyBuyer);
  const [acceptWaiver, setAcceptWaiver] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptSensitive, setAcceptSensitive] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponType, setCouponType] = useState("free");
  const [couponFinalPrice, setCouponFinalPrice] = useState(null);
  const [couponMsg, setCouponMsg] = useState("");
  const [couponChecking, setCouponChecking] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const paywallRef = useRef(null);
  const flowRef = useRef(null);

  // Stabile PayPal-Optionen: Inline-Objekte würden bei jedem Tastendruck den Script-Provider
  // neu laden -> "zoid destroyed all components" und Buttons verschwinden. Daher memoisiert.
  const paypalOptions = useMemo(() => ({
    "client-id": config?.paypal_client_id,
    currency: config?.currency || "EUR",
    intent: "capture",
    locale: "de_DE",
  }), [config?.paypal_client_id, config?.currency]);

  const ppCreateOrder = useCallback(async () => {
    const ok = await Promise.resolve(flowRef.current?.ensurePrepared?.()).catch(() => false);
    if (!ok) throw new Error("checkout-not-ready");
    const order = await createOrder(id, true);
    return order.order_id;
  }, [id]);

  const ppOnApprove = useCallback(async (data) => {
    setProcessing(true);
    try {
      const cap = await captureOrder(id, data.orderID);
      if (cap.status === "COMPLETED") { toast.success("Zahlung erfolgreich!"); nav(`/report-erstellen/${id}`); }
    } catch (e) { toast.error("Zahlung konnte nicht erfasst werden."); }
    finally { setProcessing(false); }
  }, [id, nav]);

  const ppOnError = useCallback((err) => {
    if (String(err?.message || err).includes("checkout-not-ready")) return;
    toast.error("PayPal ist gerade nicht verfügbar. Bitte nutze Karte / Klarna / SEPA.");
  }, []);

  const setB = (k, v) => setBuyer((b) => ({ ...b, [k]: v }));
  const scrollToPaywall = () => paywallRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await getConfig();
        if (!alive) return;
        setConfig(cfg);
        const data = await getTeaser(id);
        if (!alive) return;
        setTeaserData(data);
        const returning = new URLSearchParams(location.search).get("session_id");
        if (data.paid && !returning) {
          nav(data.details_completed ? `/report/${id}` : `/report-erstellen/${id}`);
        }
      } catch (e) {
        console.error(e);
        if (e?.response?.status === 404) { toast.error("Bericht nicht gefunden. Bitte starte mit deiner Idee neu."); nav("/", { replace: true }); return; }
        toast.error("Bericht konnte nicht geladen werden. Bitte Seite neu laden.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]); // eslint-disable-line

  // Return from Stripe Checkout
  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    setProcessing(true);
    const poll = async () => {
      if (cancelled) return;
      try {
        const st = await getStripeStatus(sessionId);
        if (st.payment_status === "paid") {
          toast.success("Zahlung erfolgreich! Jetzt die Detailfragen ausfüllen.");
          nav(`/report-erstellen/${id}`);
          return;
        }
        if (st.status === "expired") {
          toast.error("Zahlung abgelaufen. Bitte erneut versuchen.");
          setProcessing(false);
          nav(`/teaser/${id}`, { replace: true });
          return;
        }
      } catch (e) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 12) { setProcessing(false); toast.error("Zahlungsstatus unklar. Prüfe deine E-Mails."); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search]); // eslint-disable-line

  const validateCheckout = () => {
    const isBusiness = buyer.customer_type === "business";
    if (isBusiness && !buyer.company.trim()) { toast.warning("Bitte den Firmennamen (inkl. Rechtsform) angeben."); return false; }
    if (!buyer.first_name.trim() || !buyer.last_name.trim()) { toast.warning(isBusiness ? "Bitte den Ansprechpartner (Vor- und Nachname) angeben." : "Bitte Vor- und Nachname angeben."); return false; }
    if (!/^\S+@\S+\.\S+$/.test(buyer.email.trim())) { toast.warning("Bitte eine gültige E-Mail angeben."); return false; }
    if (!buyer.street.trim() || !/^\d{5}$/.test(buyer.zip.trim()) || !buyer.city.trim()) { toast.warning("Bitte vollständige Rechnungsadresse (Straße, PLZ, Ort) angeben."); return false; }
    if (!acceptWaiver) { toast.warning("Bitte den Verzicht auf das Widerrufsrecht bestätigen."); return false; }
    if (!acceptTerms) { toast.warning("Bitte AGB, Widerrufsbelehrung & Datenschutz akzeptieren."); return false; }
    if ((teaserData?.sensitive_categories || []).length && !acceptSensitive) { toast.warning("Bitte den Hinweis zu sensiblen Bereichen bestätigen."); return false; }
    return true;
  };

  const ensurePrepared = async () => {
    if (!validateCheckout()) return false;
    const couponCode = (couponApplied && couponType !== "free") ? coupon.trim() : "";
    await prepareCheckout(id, {
      ...buyer,
      accept_immediate_delivery: acceptWaiver,
      accept_agb: acceptTerms,
      accept_privacy: acceptTerms,
    }, acceptSensitive, couponCode);
    return true;
  };
  flowRef.current = { ensurePrepared };

  const handleStripePay = async () => {
    if (!(await ensurePrepared().catch(() => false))) return;
    setProcessing(true);
    try {
      const res = await createStripeSession(id, true);
      if (res.already_paid) { nav(`/report-erstellen/${id}`); return; }
      if (res.url) { window.location.href = res.url; return; }
      toast.error("Stripe-Zahlung konnte nicht gestartet werden.");
      setProcessing(false);
    } catch (e) {
      toast.error("Stripe-Zahlung konnte nicht gestartet werden.");
      setProcessing(false);
    }
  };

  const handleMockPay = async () => {
    if (!(await ensurePrepared().catch(() => false))) return;
    setProcessing(true);
    try {
      const order = await createOrder(id, true);
      const cap = await captureOrder(id, order.order_id);
      if (cap.status === "COMPLETED") { toast.success("Zahlung erfolgreich!"); nav(`/report-erstellen/${id}`); }
      else toast.error("Zahlung nicht abgeschlossen.");
    } catch (e) {
      toast.error("Zahlung fehlgeschlagen.");
    } finally { setProcessing(false); }
  };

  const applyCoupon = async () => {
    const code = coupon.trim();
    if (!code) { toast.warning("Bitte einen Gutscheincode eingeben."); return; }
    setCouponChecking(true);
    try {
      const res = await validateCoupon(code, teaserData?.audience === "business" ? "business" : "gruender");
      setCouponMsg(res.message || "");
      setCouponApplied(!!res.valid);
      if (res.valid) {
        const dt = res.discount_type || "free";
        setCouponType(dt);
        setCouponFinalPrice(dt === "free" ? 0 : res.final_price);
        if (dt === "free") toast.success("Gutschein aktiviert – dein Report ist gratis.");
        else toast.success(res.message || "Gutschein aktiviert.");
      } else {
        toast.error(res.message || "Gutscheincode ungültig.");
      }
    } catch (e) {
      setCouponApplied(false);
      setCouponMsg("Gutschein konnte nicht geprüft werden.");
      toast.error("Gutschein konnte nicht geprüft werden.");
    } finally { setCouponChecking(false); }
  };

  const validateCouponCheckout = () => {
    if (!/^\S+@\S+\.\S+$/.test(buyer.email.trim())) { toast.warning("Bitte eine gültige E-Mail angeben."); return false; }
    if (!acceptWaiver) { toast.warning("Bitte den Verzicht auf das Widerrufsrecht bestätigen."); return false; }
    if (!acceptTerms) { toast.warning("Bitte AGB, Widerrufsbelehrung & Datenschutz akzeptieren."); return false; }
    if ((teaserData?.sensitive_categories || []).length && !acceptSensitive) { toast.warning("Bitte den Hinweis zu sensiblen Bereichen bestätigen."); return false; }
    return true;
  };

  const handleRedeem = async () => {
    if (!validateCouponCheckout()) return;
    setRedeeming(true);
    try {
      await redeemCoupon({
        report_id: id, code: coupon.trim(), email: buyer.email.trim(), first_name: buyer.first_name.trim(),
        accept_immediate_delivery: acceptWaiver, accept_agb: acceptTerms, sensitive_ack: acceptSensitive,
      });
      toast.success("Report kostenlos freigeschaltet!");
      nav(`/report-erstellen/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gutschein konnte nicht eingelöst werden.");
      setRedeeming(false);
    }
  };

  const returningFromStripe = !!new URLSearchParams(location.search).get("session_id");
  if (returningFromStripe && processing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="stripe-confirming">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--brand-solid)" }} />
        <p className="text-slate-600">Zahlung wird bestätigt …</p>
      </div>
    );
  }

  if (loading || !teaserData || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--brand-solid)" }} />
      </div>
    );
  }

  const isBlocked = teaserData.blocked;
  const isBusiness = teaserData.audience === "business";
  const signals = teaserData.signals || [];
  const scoreMin = teaserData.score_min;
  const scoreMax = teaserData.score_max;
  const sensitiveCategories = teaserData.sensitive_categories || [];
  const REPORT_CHAPTERS = [
    "Executive Summary & Gesamteinschätzung", "Marktumfeld & Standortanalyse",
    "Wettbewerb & Positionierung", "Zielgruppe & Kundennutzen", "Chancen & Risiken",
    "Rechtsform-Orientierung", "Fördermöglichkeiten & Finanzierung", "Marketing & Vertrieb",
    "Cashflow & Wirtschaftlichkeit", "Compliance, Recht, DSGVO & Steuern",
    "Sparring-Fragen für Bank & Behörden", "Konkrete nächste Schritte (30 Tage)",
  ];
  const price = formatPrice(teaserData.price || config.price || "29.00", config.currency || "EUR");
  const couponFree = couponApplied && couponType === "free";
  const couponDiscount = couponApplied && couponType !== "free";
  const discountPrice = couponDiscount && couponFinalPrice != null
    ? formatPrice(couponFinalPrice, config.currency || "EUR")
    : price;
  const unlockLabel = isBusiness ? "Gründio Report" : "Score & Report";

  return (
    <div className={`min-h-screen ${isBusiness ? "theme-petrol" : ""}`}>
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Logo size="sm" variant={isBusiness ? "business" : "founder"} />
          {!isBlocked && (
            <Button
              data-testid="header-unlock-btn"
              onClick={scrollToPaywall}
              className="h-10 px-4 sm:px-5 rounded-xl text-sm font-semibold brand-gradient-bg text-white hover:opacity-95 shadow-sm shrink-0"
            >
              <FileText className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{unlockLabel} freischalten · </span>{price}
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Qualitative teaser */}
        <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl animate-fade-in-up">
          <div className="absolute inset-0 bg-slate-900" />
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full opacity-30 brand-gradient-bg blur-3xl" />
          <div className="relative p-6 sm:p-10 text-white">
            {!isBusiness && (
              <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2">💡 Ersteinschätzung zu deiner Geschäftsidee</div>
            )}
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              {isBusiness ? "Ersteinschätzung zu deinem Unternehmen" : "Deine kostenlose KI-Ersteinschätzung"}
            </h1>
            {teaserData.intro && (
              <p data-testid="teaser-intro" className="mt-4 text-[15px] sm:text-base leading-relaxed text-white/85">{teaserData.intro}</p>
            )}

            {/* Score-Tendenz entfernt (wird durch Beispiel-PDFs ersetzt) */}

            {!isBlocked ? (
              <div className="mt-6 space-y-3" data-testid="teaser-signals">
                {signals.map((s, i) => (
                  <div key={i} data-testid={`signal-${i}`} className="flex items-start gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
                    <span className="text-lg leading-none mt-0.5" aria-hidden>{STATUS_EMOJI[s.status] || "🟡"}</span>
                    <div>
                      <div className="font-semibold" style={{ color: STATUS_COLOR[s.status] || "#F59E0B" }}>{s.label}</div>
                      <div className="text-sm text-white/80 leading-snug">{s.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-6 text-white/80">Diese Eingabe konnten wir nicht als ernsthafte Geschäftsidee einordnen. Bitte starte mit einer konkreteren Beschreibung neu.</p>
            )}

            {!isBlocked && (
              <div data-testid="value-preview" className="mt-6">
                <div className="text-[11px] uppercase tracking-widest text-emerald-300 mb-3">Das steckt in deinem Gründio Report</div>
                <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-5 overflow-hidden">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {REPORT_CHAPTERS.map((c, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-sm text-white/85">
                        <Lock className="h-3.5 w-3.5 shrink-0 text-emerald-300/70" />
                        <span className="tabular-nums text-white/40 w-4">{i + 1}.</span>
                        <span>{c}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-xs rounded-full bg-emerald-500/15 border border-emerald-400/20 px-3 py-1 text-emerald-200">Förderprogramme für deinen Status</span>
                    <span className="text-xs rounded-full bg-emerald-500/15 border border-emerald-400/20 px-3 py-1 text-emerald-200">Konkreter 30-Tage-Plan</span>
                    <span className="text-xs rounded-full bg-emerald-500/15 border border-emerald-400/20 px-3 py-1 text-emerald-200">{isBusiness ? "Detaillierte Business-Auswertung" : "Dein exakter Score (0–100)"}</span>
                  </div>
                </div>
              </div>
            )}

            {!isBlocked && (
              <div data-testid="transparency-note" className="mt-6 rounded-xl bg-emerald-500/10 border border-emerald-400/20 px-4 py-3 text-sm text-white/85 leading-relaxed">
                📌 <b>Hinweis zur Vollständigkeit:</b> Für deinen maßgeschneiderten Report (über 10 Seiten){isBusiness ? ", deinen 30-Tage-Plan und die detaillierte Business-Auswertung" : ", deinen 30-Tage-Plan und den faktorbasierten Score (0–100)"} benötigen wir nach der Freischaltung noch ein paar Angaben (z. B. Kapital & Standort). Dauer: ca. 2 Minuten.
              </div>
            )}
          </div>
        </div>

        {isBlocked ? (
          <div className="mt-6 text-center">
            <Button onClick={() => nav("/")} className="h-12 px-6 rounded-xl font-semibold brand-gradient-bg text-white">Neue Idee eingeben</Button>
          </div>
        ) : (
          <>
            <div className="mt-6 flex flex-col items-center gap-2 animate-fade-in-up">
              <Button
                data-testid="scroll-to-paywall-btn"
                onClick={scrollToPaywall}
                className="pulse-cta h-14 px-8 rounded-xl font-semibold text-base brand-gradient-bg text-white hover:opacity-95 shadow-lg w-full sm:w-auto"
              >
                <FileText className="h-5 w-5 mr-2" /> 🚀 {isBusiness ? "Meinen Gründio Report (10+ Seiten)" : "Meinen Score & Report (10+ Seiten)"} freischalten ({couponFree ? "gratis" : couponDiscount ? discountPrice : price})
              </Button>
              <span className="text-xs text-slate-500">{couponFree ? "Sofort-Zugang · kostenlos mit Gutschein" : "Sofort-Zugang · Sichere Zahlung via PayPal & Stripe"}</span>
            </div>

            {/* Checkout */}
            <div ref={paywallRef} data-testid="paywall-card" className="relative mt-8 bg-white border border-slate-200/80 rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-2xl scroll-mt-20">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold brand-gradient-text mb-3">
                <Lock className="h-4 w-4" style={{ color: "var(--brand-solid)" }} /> Freischalten
              </div>
              {!isBusiness && config.founders_remaining > 0 && (
                <div data-testid="founder-offer-banner" className="mb-4 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs sm:text-sm text-amber-900">
                  <Flame className="h-4 w-4 shrink-0 text-amber-500" />
                  <span><b>Einführungsangebot:</b> 29 € statt 49 € · nur für die ersten {config.founder_limit} Gründer · <b data-testid="founders-remaining">noch {config.founders_remaining} frei</b></span>
                </div>
              )}
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
                {isBusiness ? "Gründio Report (über 10 Seiten)" : "Score (0–100) + Report (über 10 Seiten)"}
              </h2>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                <Bullet>{isBusiness ? "Ehrliche, qualitative Gesamteinschätzung deines Business" : "Faktorbasierter Score (0–100) & ehrliche Gesamteinschätzung"}</Bullet>
                <Bullet>Förder-Orientierung: Programme, die realistisch in Frage kommen können</Bullet>
                <Bullet>Konkrete Optimierungs- & Wachstumshinweise für den deutschen Markt</Bullet>
                <Bullet>Konkreter 30-Tage-Plan & Sparring-Fragen für Bank-/Behördengespräche</Bullet>
                <Bullet>Strukturiertes PDF zum Download &amp; per E-Mail</Bullet>
              </ul>

              {/* Gutscheincode */}
              <div className="mt-8" data-testid="coupon-box">
                <div className="text-sm font-semibold text-slate-900 mb-2">Gutscheincode</div>
                {!couponApplied ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      data-testid="coupon-input"
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                      placeholder="Code eingeben (optional)"
                      className="h-12 rounded-xl uppercase"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                    />
                    <Button
                      type="button"
                      data-testid="coupon-apply-btn"
                      onClick={applyCoupon}
                      disabled={couponChecking}
                      variant="outline"
                      className="h-12 px-5 rounded-xl font-semibold border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors shrink-0"
                    >
                      {couponChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Einlösen"}
                    </Button>
                  </div>
                ) : (
                  <div data-testid="coupon-active" className="flex items-center justify-between gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-emerald-800 font-semibold">
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      {couponFree
                        ? <>Gutschein <b>{coupon.trim()}</b> aktiv – dein Report ist gratis.</>
                        : <>Gutschein <b>{coupon.trim()}</b> aktiv – dein Preis: <b>{discountPrice}</b> statt {price}.</>}
                    </div>
                    <button
                      type="button"
                      data-testid="coupon-remove-btn"
                      onClick={() => { setCouponApplied(false); setCoupon(""); setCouponMsg(""); setCouponType("free"); setCouponFinalPrice(null); }}
                      className="text-xs text-emerald-700 underline hover:text-emerald-900 shrink-0"
                    >
                      Entfernen
                    </button>
                  </div>
                )}
                {!couponApplied && couponMsg && (
                  <p data-testid="coupon-message" className="mt-2 text-xs text-rose-600">{couponMsg}</p>
                )}
              </div>

              {/* Billing */}
              {!couponFree ? (
              <div className="mt-6">
                <div className="text-sm font-semibold text-slate-900 mb-4">Rechnungsdaten</div>

                {/* B2C / B2B toggle */}
                <div className="inline-flex p-1 mb-4 rounded-xl bg-slate-100 border border-slate-200" data-testid="buyer-type-toggle">
                  {[
                    { key: "private", label: "Privatperson" },
                    { key: "business", label: "Unternehmen / Gewerbe" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      data-testid={`buyer-type-${opt.key}`}
                      onClick={() => setBuyer((b) => ({
                        ...b,
                        customer_type: opt.key,
                        ...(opt.key === "private" ? { company: "", vat_id: "" } : {}),
                      }))}
                      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                        buyer.customer_type === opt.key
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {buyer.customer_type === "business" && (
                    <>
                      <Field label="Firmenname (inkl. Rechtsform) *" full><Input data-testid="buyer-company" value={buyer.company} onChange={(e) => setB("company", e.target.value)} placeholder="z. B. Mustermann Digital UG (haftungsbeschränkt)" className="h-12 rounded-xl" /></Field>
                      <Field label="USt-IdNr. (optional)" full><Input data-testid="buyer-vat-id" value={buyer.vat_id} onChange={(e) => setB("vat_id", e.target.value)} placeholder="z. B. DE123456789" className="h-12 rounded-xl" /></Field>
                    </>
                  )}
                  <Field label={buyer.customer_type === "business" ? "Ansprechpartner Vorname *" : "Vorname *"}><Input data-testid="buyer-first-name" value={buyer.first_name} onChange={(e) => setB("first_name", e.target.value)} className="h-12 rounded-xl" /></Field>
                  <Field label={buyer.customer_type === "business" ? "Ansprechpartner Nachname *" : "Nachname *"}><Input data-testid="buyer-last-name" value={buyer.last_name} onChange={(e) => setB("last_name", e.target.value)} className="h-12 rounded-xl" /></Field>
                  <Field label="E-Mail *" full><Input data-testid="buyer-email" type="email" value={buyer.email} onChange={(e) => setB("email", e.target.value)} placeholder="Report & Rechnung gehen an diese Adresse" className="h-12 rounded-xl" /></Field>
                  <Field label="Straße & Nr. *" full><Input data-testid="buyer-street" value={buyer.street} onChange={(e) => setB("street", e.target.value)} className="h-12 rounded-xl" /></Field>
                  <Field label="PLZ *"><Input data-testid="buyer-zip" value={buyer.zip} onChange={(e) => setB("zip", e.target.value.replace(/\D/g, ""))} maxLength={5} inputMode="numeric" className="h-12 rounded-xl" /></Field>
                  <Field label="Ort *"><Input data-testid="buyer-city" value={buyer.city} onChange={(e) => setB("city", e.target.value)} className="h-12 rounded-xl" /></Field>
                </div>
              </div>
              ) : (
              <div className="mt-6" data-testid="coupon-minimal-fields">
                <div className="text-sm font-semibold text-slate-900 mb-4">Deine Kontaktdaten (für den Report)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Vorname (optional)"><Input data-testid="buyer-first-name" value={buyer.first_name} onChange={(e) => setB("first_name", e.target.value)} className="h-12 rounded-xl" /></Field>
                  <Field label="E-Mail *" full><Input data-testid="buyer-email" type="email" value={buyer.email} onChange={(e) => setB("email", e.target.value)} placeholder="Dein Report geht an diese Adresse" className="h-12 rounded-xl" /></Field>
                </div>
                <p className="mt-2 text-xs text-slate-500">Für den kostenlosen Gründio Report brauchen wir nur deine E-Mail – keine Rechnungsadresse.</p>
              </div>
              )}

              {/* Legal */}
              <label data-testid="paywall-waiver-row" className="mt-6 flex items-start gap-3 cursor-pointer p-4 rounded-xl bg-emerald-50/70 border border-emerald-100">
                <Checkbox checked={acceptWaiver} onCheckedChange={(v) => setAcceptWaiver(!!v)} data-testid="paywall-accept-waiver" className="mt-0.5" />
                <span className="text-sm text-slate-700 leading-relaxed">
                  Ich verlange ausdrücklich die <b>sofortige Bereitstellung</b> unmittelbar nach der Zahlung und bestätige, dass mein <b>Widerrufsrecht</b> gemäß <b>§ 356 Abs. 5 BGB</b> erlischt. *
                </span>
              </label>
              <label data-testid="paywall-terms-row" className="mt-3 flex items-start gap-3 cursor-pointer p-4 rounded-xl bg-slate-50 border border-slate-200">
                <Checkbox checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} data-testid="paywall-accept-terms" className="mt-0.5" />
                <span className="text-sm text-slate-700 leading-relaxed">
                  Ich akzeptiere die{" "}
                  <a href="/agb" target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-700 underline" data-testid="paywall-agb-link">AGB</a>{" "}
                  &amp; die{" "}
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-700 underline" data-testid="paywall-privacy-link">Datenschutzerklärung</a>,
                  habe die{" "}
                  <a href="/widerruf" target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-700 underline" data-testid="paywall-widerruf-link">Widerrufsbelehrung</a>{" "}
                  zur Kenntnis genommen und stimme der Verarbeitung meiner Rechnungsdaten zur Vertragserfüllung zu. *
                </span>
              </label>

              {sensitiveCategories.length > 0 && (
                <label data-testid="paywall-sensitive-row" className="mt-3 flex items-start gap-3 cursor-pointer p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <Checkbox checked={acceptSensitive} onCheckedChange={(v) => setAcceptSensitive(!!v)} data-testid="paywall-accept-sensitive" className="mt-0.5" />
                  <span className="text-sm text-amber-900 leading-relaxed">
                    ⚠️ <b>Hinweis zu sensiblen Bereichen ({sensitiveCategories.join(", ")}):</b> Für diese Branchen gelten besondere gesetzliche Anforderungen. Mir ist bewusst, dass der Gründio Report ein automatisiert erstellter KI-Denkanstoß ist und <b>keine rechtliche, medizinische oder fachkundige Beratung</b> ersetzt. *
                  </span>
                </label>
              )}

              <div className="mt-6 pt-6 border-t border-slate-200 flex items-end justify-between flex-wrap gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">{couponFree ? "Mit Gutschein" : couponDiscount ? "Mit Gutschein" : "Einführungspreis · einmalig"}</div>
                  {couponFree ? (
                    <div className="flex items-baseline gap-3">
                      <div className="text-4xl font-extrabold tracking-tight text-emerald-600" style={{ fontFamily: "Outfit" }} data-testid="coupon-price">0 €</div>
                      <div className="text-lg font-semibold text-slate-400 line-through">{price}</div>
                    </div>
                  ) : couponDiscount ? (
                    <div className="flex items-baseline gap-3">
                      <div className="text-4xl font-extrabold tracking-tight text-emerald-600" style={{ fontFamily: "Outfit" }} data-testid="coupon-price">{discountPrice}</div>
                      <div className="text-lg font-semibold text-slate-400 line-through">{price}</div>
                    </div>
                  ) : (
                    <div className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>{price}</div>
                  )}
                  <div className="text-xs text-slate-500 mt-1">{couponFree ? "Kostenlos freigeschaltet" : couponDiscount ? "Rabattierter Endpreis · keine weiteren Kosten" : "Endpreis · keine weiteren Kosten"}</div>
                </div>

                <div className="flex flex-col items-end gap-2 min-w-[240px]">
                  {couponFree ? (
                    <Button onClick={handleRedeem} disabled={redeeming} data-testid="coupon-redeem-btn" className="pulse-cta h-14 px-6 rounded-xl font-semibold text-base w-full bg-emerald-600 text-white hover:bg-emerald-700">
                      {redeeming ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Wird freigeschaltet…</> : <><FileText className="h-5 w-5 mr-2" /> Gratis freischalten</>}
                    </Button>
                  ) : config.mock_payment ? (
                    <Button onClick={handleMockPay} disabled={processing} data-testid="paywall-pay-btn" className="pulse-cta h-14 px-6 rounded-xl font-semibold text-base w-full brand-gradient-bg text-white hover:opacity-95">
                      {processing ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Wird verarbeitet…</> : <><FileText className="h-5 w-5 mr-2" /> Jetzt freischalten</>}
                    </Button>
                  ) : (
                    <PayPalBoundary fallback={<div data-testid="paypal-fallback" className="w-full text-sm text-slate-600 border-2 border-dashed border-slate-200 rounded-xl p-4 text-center">PayPal ist gerade nicht verfügbar. Bitte nutze unten <b>Karte · Klarna · SEPA-Lastschrift</b>.</div>}>
                      <PayPalScriptProvider options={paypalOptions}>
                        <div className="w-full" data-testid="paypal-buttons-container">
                          <PayPalButtons
                            style={PP_STYLE}
                            createOrder={ppCreateOrder}
                            onApprove={ppOnApprove}
                            onError={ppOnError}
                          />
                        </div>
                      </PayPalScriptProvider>
                    </PayPalBoundary>
                  )}

                  {!couponFree && config.stripe_enabled && (
                    <>
                      <div className="flex items-center gap-3 w-full my-1">
                        <div className="h-px flex-1 bg-slate-200" />
                        <span className="text-xs text-slate-400 uppercase tracking-widest">oder</span>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                      <Button onClick={handleStripePay} disabled={processing} data-testid="stripe-pay-btn" variant="outline" className="h-14 px-6 rounded-xl font-semibold text-base w-full border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors">
                        {processing ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Weiterleitung …</> : <><CreditCard className="h-5 w-5 mr-2" /> Karte · Klarna · SEPA-Lastschrift</>}
                      </Button>
                    </>
                  )}

                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <ShieldCheck className="h-3.5 w-3.5" style={{ color: "var(--brand-solid)" }} /> {couponFree ? "Kostenlos · keine Zahlung nötig" : "Sichere Zahlung via PayPal & Stripe"}
                  </div>
                  <p className="text-[11px] leading-snug text-slate-400" data-testid="checkout-disclaimer">
                    Automatisierter, KI-gestützter Report als Orientierungshilfe – keine Rechts-, Steuer- oder Unternehmensberatung, keine Erfolgsgarantie.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--brand-solid)" }} />
      <span>{children}</span>
    </li>
  );
}
